import { useEffect, useState } from "react";
import { toast } from "sonner";
import api from "@/lib/api";
import { CheckCircle, XCircle, Database, HardDrive, EnvelopeSimple, Clock, ArrowsClockwise } from "@phosphor-icons/react";

const Stat = ({ label, value, ok, testid }) => (
  <div className="bg-white border border-slate-200 rounded-lg p-4" data-testid={testid}>
    <div className="text-xs uppercase tracking-widest text-slate-500 flex items-center gap-1">
      {ok === true && <CheckCircle size={12} className="text-emerald-600" />}
      {ok === false && <XCircle size={12} className="text-red-600" />}
      {label}
    </div>
    <div className="text-lg font-semibold mt-1 text-slate-900 break-all">{value ?? "—"}</div>
  </div>
);

export default function SystemHealth() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const load = async () => {
    setLoading(true);
    try { setData((await api.get("/system/health")).data); }
    catch (e) { toast.error(e.response?.data?.detail || "Health check failed"); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); const t = setInterval(load, 60000); return () => clearInterval(t); }, []);

  if (loading || !data) return <div className="text-sm text-slate-400 py-10 text-center" data-testid="health-loading">Loading…</div>;

  return (
    <div className="space-y-5" data-testid="system-health-page">
      <div className="flex items-center justify-between">
        <div>
          <div className="text-xs uppercase tracking-widest text-slate-400">Administration</div>
          <h2 className="text-2xl font-semibold">System Health</h2>
          <p className="text-sm text-slate-500">Live snapshot of database, storage, email, backups and uptime.</p>
        </div>
        <button onClick={load} data-testid="health-refresh"
                className="text-sm border border-slate-200 rounded-md px-3 py-2 inline-flex items-center gap-1"><ArrowsClockwise size={14} /> Refresh</button>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <Stat label="App version" value={data.app_version} testid="health-app-version" />
        <Stat label="Uptime" value={`${Math.floor(data.uptime_seconds / 60)} min`} testid="health-uptime" />
        <Stat label="Database" value={`${data.database.name} · ${data.database.ping_ms} ms`} ok={data.database.ok} testid="health-database" />
        <Stat label="Collections" value={data.database.collections_total} testid="health-collections" />
        <Stat label="Storage provider" value={data.storage.provider} testid="health-storage" />
        <Stat label="Email provider" value={data.email.provider} testid="health-email" />
        <Stat label="Last backup" value={data.backup.last_backup_at ? new Date(data.backup.last_backup_at).toLocaleString() : "never"} ok={!!data.backup.last_backup_at} testid="health-backup" />
        <Stat label="Backup size" value={data.backup.last_bytes ? `${(data.backup.last_bytes / 1024 / 1024).toFixed(1)} MB` : "—"} testid="health-backup-size" />
      </div>

      <div className="bg-white border border-slate-200 rounded-lg p-4" data-testid="health-counts">
        <div className="text-sm font-semibold mb-3 flex items-center gap-1"><Database size={14} /> Collection counts</div>
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3 text-sm">
          {Object.entries(data.database.counts).map(([k, v]) => (
            <div key={k} className="flex justify-between border-b border-slate-100 pb-1">
              <span className="text-slate-500">{k}</span><span className="tabular-nums font-medium">{v}</span>
            </div>
          ))}
        </div>
      </div>

      <div className="bg-white border border-slate-200 rounded-lg p-4" data-testid="health-recent-errors">
        <div className="text-sm font-semibold mb-3 flex items-center gap-1"><Clock size={14} /> Recent notable events (last 5)</div>
        {(data.recent_errors || []).length === 0 ? (
          <div className="text-xs text-slate-400">No recent errors.</div>
        ) : (
          <ul className="text-xs space-y-1">
            {data.recent_errors.map((e, i) => (
              <li key={`${e.timestamp || i}-${e.action || ""}-${e.entity || ""}`} className="text-slate-600">
                <span className="text-slate-400">{e.timestamp?.slice(0, 19)}</span> · <b>{e.action}</b> {e.entity} by {e.user_name || "?"}
              </li>
            ))}
          </ul>
        )}
      </div>

      <div className="text-[10px] text-slate-400 text-right" data-testid="health-generated-at">
        Generated at {new Date(data.generated_at).toLocaleString()}
      </div>
    </div>
  );
}
